import React from 'react'
import {
  Box,
  Flex,
  useBreakpointValue,
} from '@chakra-ui/react'
import { useTranslation } from 'react-i18next'
import { DashboardNavbar } from './DashboardNavbar'
import { DashboardSidebar } from './DashboardSidebar'
import ProSidebar from '../Pro/ProSidebar'
import { useViewAs } from '../../components/ui/ViewAsBanner'
import { ClientViewBanner } from '../Pro/ClientViewBanner'
import { useClientView } from '../../contexts/ClientViewContext'
import { useNavigationState } from '../../hooks/useNavigationState'

interface DashboardLayoutProps {
  children: React.ReactNode
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const { t } = useTranslation()
  const { isSidebarOpen, toggleSidebar } = useNavigationState()
  const { isViewAsMode } = useViewAs()
  const { isViewingClient } = useClientView()
  const isMobile = useBreakpointValue({ base: true, md: false })
  
  // Calculate top padding: 64px navbar + optional banners (40px each)
  const topPadding = isViewAsMode ? "112px" : (isViewingClient ? "104px" : "64px")
  

  
  return (
    <Box minH="100vh" bg="bg" overflow="hidden">
      {/* Skip to main content link for accessibility */}
      <Box
        as="a"
        position="absolute"
        left="-9999px"
        zIndex="tooltip"
        py={2}
        px={4}
        bg="brand.500"
        color="white"
        textDecoration="none"
        borderRadius="md"
        _focus={{
          left: "4",
          top: "4",
        }}
        onClick={(e: React.MouseEvent) => {
          e.preventDefault()
          document.getElementById('main-content')?.focus()
        }}
      >
        {t('dashboard.sidebar.skip_to_main')}
      </Box>

      {/* Top Navbar */}
      <DashboardNavbar 
        onMenuClick={toggleSidebar}
        showMenuButton={true} // Always show the hamburger menu button
      />

      {/* Client View Banner - shows when Pro user is viewing client data */}
      <ClientViewBanner />

      <Flex maxW="100vw" h="100vh">
        {/* Sidebar - Use ProSidebar when viewing client data to maintain Pro context */}
        {isViewingClient ? (
          <ProSidebar
            isOpen={isSidebarOpen}
            onClose={toggleSidebar}
            variant={isMobile ? 'drawer' : 'sidebar'}
            isCollapsed={!isSidebarOpen}
          />
        ) : (
          <DashboardSidebar
            isOpen={isSidebarOpen}
            onClose={() => toggleSidebar()}
            variant={isMobile ? 'drawer' : 'sidebar'}
            isCollapsed={!isSidebarOpen}
          />
        )}

        {/* Main Content */}
        <Box
          flex="1"
          ml={isMobile ? 0 : (isSidebarOpen ? '240px' : '64px')}
          transition="margin-left 0.3s ease"
          w="full"
          minW={0}
          maxW={isMobile ? '100vw' : 'none'}
          overflowY="auto"
          overflowX="hidden"
          pb="var(--lw-bottom-banner-height, 0px)"
        >
          <Box
            as="main"
            id="main-content"
            pt={topPadding}
            minH={`calc(100vh - ${topPadding})`}
            role="main"
            aria-label="Dashboard main content"
          >
            <Box maxW="1600px" mx="auto" w="full">
              {children}
            </Box>
          </Box>
        </Box>
      </Flex>
    </Box>
  )
}
